/* eslint-disable @typescript-eslint/no-explicit-any */
/* Page sizes (v3.0 "multiple scales"). Each page owns its canvas {width,height,columns,rowHeight,gap}.
 * Changing the size keeps the 24-column grid and rescales the ROWS proportionally, so the layout keeps
 * its shape; "Duplicate page as A4 / TV" clones a page at another size and links the two through
 * page.layoutGroup / variantOf / variantLabel (metadata the viewer uses for a "TV | A4" switch).
 * Pure functions: see web/scripts/page-size.check.ts. */
import { GRID, isBarWidget, rowsFor, uid, type SDef, type SPage, type SWidget } from "./model";

export interface Canvas { width: number; height: number; columns: number; rowHeight: number; gap: number; [k: string]: any }
export interface SizePreset { id: string; label: string; short: string; width: number; height: number; hint: string }

export const SIZE_PRESETS: SizePreset[] = [
  { id: "tv", label: "TV (16:9)", short: "TV", width: 1848, height: 944, hint: "Office TVs and the browser viewer" },
  { id: "a4l", label: "A4 landscape", short: "A4", width: 1123, height: 794, hint: "Printing and PDF, wide" },
  { id: "a4p", label: "A4 portrait", short: "A4 portrait", width: 794, height: 1123, hint: "Printing and PDF, tall" },
  { id: "square", label: "Square", short: "Square", width: 1000, height: 1000, hint: "Social posts, small screens" },
];

export function canvasFor(width: number, height: number, from?: Canvas): Canvas {
  const w = Math.max(320, Math.min(4000, Math.round(width)));
  const h = Math.max(240, Math.min(4000, Math.round(height)));
  // Narrow pages get a tighter gap so 24 columns stay usable.
  const gap = w < 900 ? 4 : 8;
  return { ...(from ?? {}), width: w, height: h, columns: from?.columns ?? GRID.columns, rowHeight: from?.rowHeight ?? GRID.rowHeight, gap };
}

export function presetOf(c: { width: number; height: number } | undefined): SizePreset | null {
  const cv = c ?? { width: GRID.width, height: GRID.height };
  return SIZE_PRESETS.find((p) => p.width === cv.width && p.height === cv.height) ?? null;
}

export function sizeLabel(c: { width: number; height: number } | undefined): string {
  const p = presetOf(c);
  const cv = c ?? { width: GRID.width, height: GRID.height };
  return p ? `${p.label} (${p.width}x${p.height})` : `Custom (${cv.width}x${cv.height})`;
}

export function shortLabel(c: { width: number; height: number } | undefined): string {
  return presetOf(c)?.short ?? "Custom";
}

function rowsOfCanvas(c: Canvas): number {
  return Math.floor((c.height + c.gap) / (c.rowHeight + c.gap));
}

/** Map [start, start+len) in `from` units to `to` units with consistent boundaries (no new overlaps). */
function mapSpan(start: number, len: number, factor: number, min: number): [number, number] {
  const a = Math.round(start * factor);
  const b = Math.round((start + len) * factor);
  return [a, Math.max(min, b - a)];
}

export interface Rescaled { widgets: SWidget[]; overflow: string[]; rows: number }

/** Proportionally re-lay the page's visuals for a new canvas. `overflow` = ids that end below the new page height. */
export function rescaleWidgets(widgets: SWidget[], from: Canvas, to: Canvas, rescale = true): Rescaled {
  const fc = from.columns || GRID.columns;
  const tc = to.columns || GRID.columns;
  const fr = rowsOfCanvas(from);
  const tr = rowsOfCanvas(to);
  const fx = rescale ? tc / fc : 1;
  const fy = rescale ? tr / fr : 1;
  const out = widgets.map((w) => {
    if (isBarWidget(w)) return w;
    const [x, ww] = mapSpan(w.layout.x, w.layout.w, fx, 1);
    const [y, hh] = mapSpan(w.layout.y, w.layout.h, fy, 1);
    const x2 = Math.max(0, Math.min(tc - ww, x));
    return { ...w, layout: { ...w.layout, x: x2, w: Math.min(ww, tc), y, h: hh } };
  });
  const overflow = out.filter((w) => !isBarWidget(w) && w.layout.y + w.layout.h > tr).map((w) => w.id);
  return { widgets: out, overflow, rows: tr };
}

/** Change this page's size in place. */
export function resizePage(page: SPage, width: number, height: number, rescale = true): { page: SPage; overflow: string[] } {
  const from = (page.canvas ?? canvasFor(GRID.width, GRID.height)) as Canvas;
  const to = canvasFor(width, height, from);
  const r = rescaleWidgets(page.widgets, from, to, rescale);
  return { page: { ...page, canvas: to, widgets: r.widgets }, overflow: r.overflow };
}

/** Clone `pageId` at another size, linked to the original. Returns the new definition and the clone's id. */
export function duplicatePageAs(def: SDef, pageId: string, width: number, height: number, label: string): { def: SDef; newId: string; overflow: string[] } | null {
  const src = def.pages.find((p) => p.id === pageId);
  if (!src) return null;
  const from = (src.canvas ?? canvasFor(GRID.width, GRID.height)) as Canvas;
  const to = canvasFor(width, height, from);
  const r = rescaleWidgets(structuredClone(src.widgets), from, to, true);
  const group = (src as any).layoutGroup || `grp-${src.id}`;
  const newId = `page-${Math.random().toString(36).slice(2, 7)}`;
  const clone: SPage = {
    ...structuredClone(src),
    id: newId,
    name: `${src.name} (${label})`,
    canvas: to,
    widgets: r.widgets.map((w) => ({ ...w, id: uid("w"), locked: false })),
    layoutGroup: group,
    variantOf: src.id,
    variantLabel: label,
  } as SPage;
  delete (clone as any).parentPageId;
  const pages: SPage[] = [];
  for (const p of def.pages) {
    if (p.id === src.id) {
      pages.push({ ...p, layoutGroup: group, variantLabel: (p as any).variantLabel || shortLabel(src.canvas) } as SPage, clone);
    } else pages.push(p);
  }
  return { def: { ...def, pages: pages.map((p, i) => ({ ...p, order: i })) }, newId, overflow: r.overflow };
}

/** Pages sharing a layoutGroup with this one (including itself), for a "TV | A4" switch. */
export function variantsOf(def: SDef, page: SPage): SPage[] {
  const g = (page as any).layoutGroup;
  return g ? def.pages.filter((p) => (p as any).layoutGroup === g) : [page];
}

export { rowsFor };
